import styled from '@emotion/styled';
import { Card, message } from 'antd';
import Empty from 'components/Empty';
import useModal from 'hooks/useModal';
import { useRef, useState, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import ConversationModal from './components/ConversationModal';
import DashboardChat from './components/DashboardChart';
import DashboardDetail from './components/DashboardDetail';
import DashboardToolbar from './components/DashboardToolbar';
import FilterForm, { defaultInitialValues, FilterFormValues } from './components/FilterForm';
import useDetailRecord from './hooks/useDetailRecord';
import useProfileRecord from './hooks/useProfileRecord';

const Container = styled.div`
  height: 100%;
  padding: 24px;
  overflow: auto;
  > * {
    margin-bottom: 16px;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 16px;
  background: var(--fett-bg-panel);
  border-radius: 2px;
  > div {
    width: 100%;
  }
`;

const isProfileSearchDirty = (values: FilterFormValues, initialValues: FilterFormValues) => {
  return (
    values.platformRegion !== initialValues.platformRegion ||
    values.scene !== initialValues.scene ||
    values.startTime !== initialValues.startTime ||
    values.endTime !== initialValues.endTime ||
    values.timeZone !== initialValues.timeZone
  );
};

function Dashboard() {
  const { t } = useTranslate();

  const [filterValues, setFilterValues] = useState(defaultInitialValues);

  const { visible, show, hide, meta } = useModal<Remind.DetailRecord>();

  const {
    list: profileRecordList,
    loading: profileRecordLoading,
    fetchList: fetchProfileList,
  } = useProfileRecord();

  const {
    list: detailRecordList,
    loading: detailRecordLoading,
    page: detailRecordPage,
    fetchList: fetchDetailRecord,
  } = useDetailRecord();

  const search = (filter: Remind.DetailFilter) => {
    const { platformRegion, scene, startTime, endTime, timeZone } = filter;

    const profileFilter = {
      platformRegion,
      scene,
      startTime,
      endTime,
      timeZone,
    };

    return Promise.all([fetchProfileList(profileFilter), fetchDetailRecord(filter)]).catch(
      (err) => {
        message.error(t('remind.网络错误，请稍后再试'));

        return Promise.reject(err);
      },
    );
  };

  const refsCurrent = {
    filterValues,
    search,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { filterValues, search },
    } = refs;

    search({
      ...filterValues,
      pageNo: 1,
      pageSize: 10,
    });
  }, []);

  return (
    <FilterForm
      initialValues={filterValues}
      onSubmit={(values, helpers) => {
        const searchValues = {
          ...values,
          platformRegion: values.platformRegion.filter(({ region }) => region.length > 0),
          pageNo: 1,
          pageSize: 10,
        };

        return (
          isProfileSearchDirty(values, filterValues)
            ? search(searchValues)
            : fetchDetailRecord(searchValues)
        )
          .then(() => {
            setFilterValues(values);
          })
          .catch(() => {
            helpers.resetForm();
          });
      }}
    >
      <Container>
        <Header>
          <DashboardToolbar />
        </Header>
        <Card title={t('remind.站内提醒')}>
          {profileRecordList && profileRecordList.length > 0 ? (
            <DashboardChat
              loading={profileRecordLoading}
              data={profileRecordList}
              range={[filterValues.startTime, filterValues.endTime]}
            />
          ) : (
            <Empty />
          )}
        </Card>
        <DashboardDetail
          loading={detailRecordLoading}
          dataSource={detailRecordList}
          pagination={detailRecordPage}
          onChange={(pagination) => {
            const { current: pageNo = 1, pageSize = 10 } = pagination;

            if (pageNo === detailRecordPage.current && pageSize === detailRecordPage.pageSize) {
              return;
            }

            fetchDetailRecord({
              ...filterValues,
              platformRegion: filterValues.platformRegion.filter(({ region }) => region.length > 0),
              pageNo,
              pageSize,
            }).catch(() => {});
          }}
          onViewConversation={show}
        />
        {meta && <ConversationModal visible={visible} onCancel={() => hide()} record={meta} />}
      </Container>
    </FilterForm>
  );
}

export default Dashboard;
